import { accuMultiplier, ticksToReach, type RushRound } from '../accumulator';

/**
 * Rush history in plain numbers for the Análise page: how far rounds go, how often a target is
 * reached, and how short/long rounds follow each other. Pure: the page and the tests run it on the
 * recorded rounds. Descriptive only: past rounds don't decide the next one.
 */

/** Profit targets the reach table shows (% of the stake). */
export const RUSH_REACH_TARGETS = [10, 20, 50, 100, 200, 500, 1000] as const;

/** The three color bands of the history: up to +99%, +100% to +999%, +1.000% or more. */
export function rushBand(rate: number, ticks: number): 0 | 1 | 2 {
  const pct = (accuMultiplier(rate, ticks) - 1) * 100 + 1e-9;
  return pct >= 1000 ? 2 : pct >= 100 ? 1 : 0;
}

export interface RushReachRow {
  /** Target profit, %. */
  pct: number;
  /** Ticks needed to reach it at this growth. */
  ticks: number;
  /** Rounds that reached it. */
  hits: number;
  /** 0–1 of the rounds. */
  share: number;
}

export interface RushAfterShortRow {
  /** Rounds in a row that fell before the target. */
  shorts: number;
  /** Times that happened (with a round after it). */
  seen: number;
  /** Of those, the next round reached the target. */
  nextLong: number;
}

export interface RushAnalysis {
  total: number;
  growthRate: number;
  /** Target used for Longa / Curta (the cash-out), in ticks and as profit multiple. */
  targetTicks: number;
  targetMultiple: number;
  medianTicks: number;
  meanTicks: number;
  longestTicks: number;
  /** Rounds per band [até +99%, +100–999%, +1.000%+]. */
  bands: [number, number, number];
  reach: RushReachRow[];
  /** Rounds that reached the target. */
  longs: number;
  /** Current streak, newest round included. */
  current: { long: boolean; length: number } | null;
  longestShortRun: number;
  longestLongRun: number;
  /** "Depois de N curtas seguidas, a próxima chegou ao alvo": N = 1…6 (6 = 6 or more). */
  afterShorts: RushAfterShortRow[];
}

export const RUSH_AFTER_CAP = 6;

/** `rounds` oldest first. */
export function analyzeRush(rounds: readonly RushRound[], growthRate: number, targetTicks: number): RushAnalysis {
  const ticks = rounds.map((r) => r.ticks);
  const sorted = [...ticks].sort((a, b) => a - b);
  const total = ticks.length;
  const bands: [number, number, number] = [0, 0, 0];
  for (const t of ticks) bands[rushBand(growthRate, t)]++;
  const reach = RUSH_REACH_TARGETS.map((pct) => {
    const need = ticksToReach(growthRate, 1 + pct / 100);
    const hits = ticks.filter((t) => t >= need).length;
    return { pct, ticks: need, hits, share: total ? hits / total : 0 };
  });

  const long = ticks.map((t) => t >= targetTicks);
  let longestShortRun = 0;
  let longestLongRun = 0;
  let run = 0;
  for (let i = 0; i < long.length; i++) {
    run = i > 0 && long[i] === long[i - 1] ? run + 1 : 1;
    if (long[i]) longestLongRun = Math.max(longestLongRun, run);
    else longestShortRun = Math.max(longestShortRun, run);
  }
  const current = total ? { long: long[total - 1]!, length: run } : null;

  // After k shorts in a row (k = how many shorts end at round i), what did round i+1 do?
  const afterShorts: RushAfterShortRow[] = Array.from({ length: RUSH_AFTER_CAP }, (_, k) => ({ shorts: k + 1, seen: 0, nextLong: 0 }));
  let shortRun = 0;
  for (let i = 0; i < long.length - 1; i++) {
    shortRun = long[i] ? 0 : shortRun + 1;
    if (shortRun === 0) continue;
    const row = afterShorts[Math.min(shortRun, RUSH_AFTER_CAP) - 1]!;
    row.seen++;
    if (long[i + 1]) row.nextLong++;
  }

  return {
    total,
    growthRate,
    targetTicks,
    targetMultiple: accuMultiplier(growthRate, targetTicks),
    medianTicks: total ? sorted[Math.floor(total / 2)]! : 0,
    meanTicks: total ? ticks.reduce((a, b) => a + b, 0) / total : 0,
    longestTicks: total ? sorted[total - 1]! : 0,
    bands,
    reach,
    longs: long.filter(Boolean).length,
    current,
    longestShortRun,
    longestLongRun,
    afterShorts,
  };
}

/** The three history colors as stored sides: cinza (até +99%), verde (+100% a +999%), laranja (+1.000% ou mais). */
export const RUSH_BAND_SIDES = ['GRAY', 'GREEN', 'ORANGE'] as const;
export type RushBandSide = (typeof RUSH_BAND_SIDES)[number];

export interface RushBandRun {
  side: RushBandSide;
  length: number;
  /** Take-off of the run's first round and crash of its last (epoch s). */
  startAt: number;
  endAt: number;
}

/**
 * Longest run of rounds of each color in a row (`rounds` oldest first). A missing round (its id
 * skipped, e.g. a recorder restart) ends the run, so two stretches are never glued together.
 * On a tie the earliest run keeps the title.
 */
export function longestRushBandRuns(rounds: readonly RushRound[], growthRate: number): [RushBandRun | null, RushBandRun | null, RushBandRun | null] {
  const best: [RushBandRun | null, RushBandRun | null, RushBandRun | null] = [null, null, null];
  let band = -1;
  let length = 0;
  let startAt = 0;
  let prevId: number | null = null;
  for (const r of rounds) {
    const b = rushBand(growthRate, r.ticks);
    if (b === band && prevId !== null && r.id === prevId + 1) length++;
    else {
      band = b;
      length = 1;
      startAt = r.takeoff;
    }
    prevId = r.id;
    const cur = best[b];
    if (!cur || length > cur.length) best[b] = { side: RUSH_BAND_SIDES[b]!, length, startAt, endAt: r.crashedAt };
  }
  return best;
}
